import type { SidebarThreadSummary, ThreadSession } from "~/types";
import { isSessionActivelyRunning } from "~/lib/threadRuntimeState";

// ---------------------------------------------------------------------------
// Task <-> Forge thread live-status link.
//
// Resolves, for a given CMMD task id, the live Forge thread executing it and
// its derived agent status. The link is established by anchored sidebar thread
// summaries (anchorKind === "task" + anchorCmmdId), NOT by the numeric
// CmmdBoardTask.threadId — that id belongs to a different keyspace and cannot
// index the Forge store.
//
// Extracted from homebase/unifiedWorkItems.ts so both the homebase feed and the
// CMMD project task surface share one implementation without importing each
// other. Operates purely on SidebarThreadSummary — no WorkItem dependency.
// ---------------------------------------------------------------------------

export type AgentStatus =
  | "running"
  | "idle"
  | "error"
  | "pending-approval"
  | "pending-input"
  | "none";

export function deriveAgentStatus(summary: SidebarThreadSummary): AgentStatus {
  if (summary.hasPendingApprovals) return "pending-approval";
  if (summary.hasPendingUserInput) return "pending-input";

  const session = summary.session;
  if (!session) return "none";

  if (session.status === "error") return "error";
  if (session.status === "running") return "running";
  if (session.status === "ready" || session.status === "connecting") return "idle";

  return "none";
}

export function deriveAgentProvider(session: ThreadSession | null): string | null {
  if (!session) return null;
  return session.provider === "codex"
    ? "Codex"
    : session.provider === "claudeAgent"
      ? "Claude"
      : session.provider;
}

export function resolveActivityTimestamp(summary: SidebarThreadSummary): string {
  return summary.latestUserMessageAt ?? summary.updatedAt ?? summary.createdAt;
}

function normalizeLookupToken(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function stripCmmdPrefix(value: string): string {
  return value.startsWith("cmmd:") ? value.slice("cmmd:".length) : value;
}

function stripTaskPrefix(value: string): string {
  const hyphenPrefix = "task-";
  if (value.startsWith(hyphenPrefix)) {
    return value.slice(hyphenPrefix.length);
  }
  const colonPrefix = "task:";
  if (value.startsWith(colonPrefix)) {
    return value.slice(colonPrefix.length);
  }
  return value;
}

function buildTaskAnchorLookupKeys(rawTaskId: unknown): readonly string[] {
  const normalized = normalizeLookupToken(rawTaskId);
  if (normalized.length === 0) return [];
  const withoutCmmdPrefix = stripCmmdPrefix(normalized);
  const base = stripTaskPrefix(withoutCmmdPrefix);
  const prefixedNumericMatch = withoutCmmdPrefix.match(/^[a-z]+[_:-](\d+)$/i);
  const numericId = prefixedNumericMatch?.[1] ?? null;
  const keys = new Set<string>();
  keys.add(normalized);
  keys.add(withoutCmmdPrefix);
  keys.add(base);
  keys.add(`task-${base}`);
  keys.add(`task:${base}`);
  if (numericId) {
    keys.add(numericId);
    keys.add(`task-${numericId}`);
    keys.add(`task:${numericId}`);
  }
  return Array.from(keys.values());
}

function matchesTaskAnchor(summary: SidebarThreadSummary, rawTaskId: unknown): boolean {
  if (summary.anchorKind !== "task" || !summary.anchorCmmdId) return false;
  const taskKeys = new Set(buildTaskAnchorLookupKeys(rawTaskId));
  return buildTaskAnchorLookupKeys(summary.anchorCmmdId).some((key) => taskKeys.has(key));
}

function compareLinkedThreadCandidate(
  left: SidebarThreadSummary,
  right: SidebarThreadSummary,
): number {
  const leftRunning = isSessionActivelyRunning(left.session);
  const rightRunning = isSessionActivelyRunning(right.session);
  if (leftRunning !== rightRunning) {
    return leftRunning ? 1 : -1;
  }
  const leftActivity = Date.parse(resolveActivityTimestamp(left));
  const rightActivity = Date.parse(resolveActivityTimestamp(right));
  const safeLeftActivity = Number.isFinite(leftActivity) ? leftActivity : 0;
  const safeRightActivity = Number.isFinite(rightActivity) ? rightActivity : 0;
  if (safeLeftActivity !== safeRightActivity) {
    return safeLeftActivity - safeRightActivity;
  }
  return left.id.localeCompare(right.id);
}

export function buildLinkedThreadByTaskId(
  threadSummaries: readonly SidebarThreadSummary[],
): ReadonlyMap<string, SidebarThreadSummary> {
  const linkedByTaskId = new Map<string, SidebarThreadSummary>();
  for (const summary of threadSummaries) {
    if (summary.archivedAt !== null) continue;
    if (summary.kind === "worker") continue;
    if (summary.anchorKind !== "task") continue;
    if (!summary.anchorCmmdId) continue;
    for (const taskIdKey of buildTaskAnchorLookupKeys(summary.anchorCmmdId)) {
      const previous = linkedByTaskId.get(taskIdKey);
      if (!previous || compareLinkedThreadCandidate(summary, previous) > 0) {
        linkedByTaskId.set(taskIdKey, summary);
      }
    }
  }
  return linkedByTaskId;
}

export function resolveLinkedTaskThread(
  linkedByTaskId: ReadonlyMap<string, SidebarThreadSummary>,
  rawTaskId: unknown,
): SidebarThreadSummary | null {
  for (const key of buildTaskAnchorLookupKeys(rawTaskId)) {
    const match = linkedByTaskId.get(key);
    if (match) return match;
  }
  return null;
}

// ---------------------------------------------------------------------------
// High-level API for the CMMD project task surface.
// ---------------------------------------------------------------------------

export interface ResolvedTaskThread {
  readonly thread: SidebarThreadSummary;
  readonly agentStatus: AgentStatus;
  readonly agentProvider: string | null;
}

export interface ResolvedTaskThreadHistoryGroup {
  readonly thread: ResolvedTaskThread;
  readonly workers: readonly ResolvedTaskThread[];
}

function resolveThreadSummary(summary: SidebarThreadSummary): ResolvedTaskThread {
  return {
    thread: summary,
    agentStatus: deriveAgentStatus(summary),
    agentProvider: deriveAgentProvider(summary.session),
  };
}

function compareActivityNewestFirst(
  left: SidebarThreadSummary,
  right: SidebarThreadSummary,
): number {
  const leftActivity = Date.parse(resolveActivityTimestamp(left));
  const rightActivity = Date.parse(resolveActivityTimestamp(right));
  const safeLeftActivity = Number.isFinite(leftActivity) ? leftActivity : 0;
  const safeRightActivity = Number.isFinite(rightActivity) ? rightActivity : 0;
  if (safeLeftActivity !== safeRightActivity) return safeRightActivity - safeLeftActivity;
  return right.id.localeCompare(left.id);
}

/**
 * Return the complete, navigable Forge history for a CMMD task. Unlike the live
 * board index, history intentionally includes archived masters and their worker
 * threads so the workbench can show prior agents without changing the current
 * live-status resolver.
 */
export function resolveTaskThreadHistory(
  threadSummaries: readonly SidebarThreadSummary[],
  rawTaskId: unknown,
): readonly ResolvedTaskThreadHistoryGroup[] {
  const masterThreads = threadSummaries
    .filter((summary) => summary.kind !== "worker" && matchesTaskAnchor(summary, rawTaskId))
    .toSorted(compareActivityNewestFirst);
  const groupedThreadIds = new Set(masterThreads.map((summary) => summary.id));
  const workersByParent = new Map<string, SidebarThreadSummary[]>();
  const directlyAnchoredWorkers: SidebarThreadSummary[] = [];

  for (const summary of threadSummaries) {
    if (summary.kind !== "worker") continue;
    if (summary.parentThreadId && groupedThreadIds.has(summary.parentThreadId)) {
      const workers = workersByParent.get(summary.parentThreadId) ?? [];
      workers.push(summary);
      workersByParent.set(summary.parentThreadId, workers);
      groupedThreadIds.add(summary.id);
    } else if (matchesTaskAnchor(summary, rawTaskId)) {
      directlyAnchoredWorkers.push(summary);
    }
  }

  const groups = masterThreads.map((summary) => ({
    thread: resolveThreadSummary(summary),
    workers: (workersByParent.get(summary.id) ?? [])
      .toSorted(compareActivityNewestFirst)
      .map(resolveThreadSummary),
  }));

  return [
    ...groups,
    ...directlyAnchoredWorkers.toSorted(compareActivityNewestFirst).map((summary) => ({
      thread: resolveThreadSummary(summary),
      workers: [],
    })),
  ];
}

/** Build the reverse index once, reuse across many tasks (board rendering). */
export function buildTaskThreadIndex(
  threadSummaries: readonly SidebarThreadSummary[],
): ReadonlyMap<string, SidebarThreadSummary> {
  return buildLinkedThreadByTaskId(threadSummaries);
}

/** Resolve a single task's live thread + derived status from a prebuilt index. */
export function resolveTaskThreadFromIndex(
  index: ReadonlyMap<string, SidebarThreadSummary>,
  rawTaskId: unknown,
): ResolvedTaskThread | null {
  const thread = resolveLinkedTaskThread(index, rawTaskId);
  if (!thread) return null;
  return resolveThreadSummary(thread);
}

/** Convenience one-shot for single lookups (tests, detail panel). */
export function resolveTaskThread(
  threadSummaries: readonly SidebarThreadSummary[],
  rawTaskId: unknown,
): ResolvedTaskThread | null {
  return resolveTaskThreadFromIndex(buildTaskThreadIndex(threadSummaries), rawTaskId);
}
